<div class="container-fluid">
  <app-main-navbar></app-main-navbar>

  <div class="dashboard-header">
    <h4>SIA Statistics Dashboard</h4>
  </div>

  <div class="dashboard-content">
    <!-- Loading -->
    <div *ngIf="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p style="margin-top: 20px; color: #666;">Loading statistics...</p>
    </div>

    <!-- Error -->
    <div *ngIf="error" class="error-container">
      <div class="alert alert-danger">
        <p style="margin: 0 0 15px 0;">{{ error }}</p>
        <button (click)="loadData()" class="btn btn-primary">Retry</button>
      </div>
    </div>

    <div *ngIf="!loading && !error">
      <!-- Info notice -->
      <div class="alert alert-info mb-3" role="alert" style="background-color: #e7f3ff; border-left: 4px solid #5e1075; border-radius: 4px;">
        <strong style="color: #5e1075; margin-right: 8px;">Note:</strong>
        Historical data is only available from <strong>November 12, 2025</strong> onwards.
      </div>

      <!-- Filters -->
      <div class="filter-bar">
        <div class="row align-items-end">
          <div class="col-md-2 mb-2">
            <label class="form-label">From Date</label>
            <input type="date" class="form-control" [(ngModel)]="dateFrom"
              (change)="onFilterChange()" [max]="dateTo || getToday()" />
          </div>
          <div class="col-md-2 mb-2">
            <label class="form-label">To Date</label>
            <input type="date" class="form-control" [(ngModel)]="dateTo"
              (change)="onFilterChange()" [max]="getToday()" [min]="dateFrom || ''" />
          </div>
          <div class="col-md-1 mb-2">
            <button class="btn btn-outline-secondary w-100" (click)="clearDateFilter()">Clear</button>
          </div>
          <div class="col-md-3 mb-2">
            <label class="form-label">Search User</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="Name or organisation..."
                [(ngModel)]="userSearch" />
              <button *ngIf="userSearch" class="btn btn-outline-secondary" (click)="clearSearch()">&times;</button>
            </div>
          </div>
          <div class="col-md-3 mb-2 d-flex align-items-end" style="padding-bottom: 4px;">
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" role="switch" id="excludeQuantipToggle"
                [(ngModel)]="excludeQuantipUsers" (change)="onFilterChange()" style="cursor: pointer;" />
              <label class="form-check-label" for="excludeQuantipToggle" style="cursor: pointer; font-weight: 500; color: #5e1075;">
                Exclude Quantip Users
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Overall Activity Graph -->
      <div class="chart-section" *ngIf="activityChart">
        <plotly-plot [data]="activityChart.data" [layout]="activityChart.layout" [config]="chartConfig"></plotly-plot>
      </div>

      <div *ngIf="!activityChart && !loading" class="no-data-message mb-3">
        <p>No activity data for the selected period.</p>
      </div>

      <!-- Users Table -->
      <div class="users-section">
        <h5 class="section-title">User Statistics</h5>

        <div *ngIf="filteredUsers().length === 0" class="no-data-message">
          <p>No users found.</p>
        </div>

        <div *ngIf="filteredUsers().length > 0" class="table-container">
          <table class="table table-condensed table-hover">
            <thead>
              <tr>
                <th>User</th>
                <th>Organisation</th>
                <th>Total</th>
                <th>Active</th>
                <th>Search Type</th>
                <th>Top Searches</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let user of filteredUsers()">
                <tr (click)="toggleUser(user.user_id)" style="cursor: pointer;">
                  <td><strong>{{ user.username }}</strong></td>
                  <td>{{ user.organisation }}</td>
                  <td>{{ user.total_tabs }}</td>
                  <td>{{ user.active_tabs }}</td>
                  <td>
                    <span class="badge badge-company">C: {{ user.company_searches }}</span>
                    <span class="badge badge-theme">T: {{ user.theme_searches }}</span>
                  </td>
                  <td>
                    <small class="text-muted">
                      <span *ngFor="let c of user.top_companies.slice(0, 2); let last = last">
                        {{ c.name }} ({{ c.count }})<span *ngIf="!last">, </span>
                      </span>
                      <span *ngIf="user.top_companies.length > 0 && user.top_themes.length > 0"> | </span>
                      <span *ngFor="let t of user.top_themes.slice(0, 2); let last = last">
                        {{ t.name }} ({{ t.count }})<span *ngIf="!last">, </span>
                      </span>
                    </small>
                  </td>
                  <td>
                    <span class="expand-arrow">{{ expandedUserId === user.user_id ? '▼' : '▶' }}</span>
                  </td>
                </tr>

                <!-- Expanded detail row -->
                <tr *ngIf="expandedUserId === user.user_id" class="expanded-detail">
                  <td colspan="7">
                    <div class="detail-panel">
                      <!-- Loading tabs -->
                      <div *ngIf="loadingTabs" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <span class="ms-2 text-muted">Loading tab details...</span>
                      </div>

                      <div *ngIf="!loadingTabs">
                        <!-- User activity graph -->
                        <div *ngIf="userActivityChart" class="mb-3">
                          <plotly-plot [data]="userActivityChart.data" [layout]="userActivityChart.layout" [config]="chartConfig"></plotly-plot>
                        </div>

                        <!-- Top searches summary -->
                        <div class="row mb-3" *ngIf="user.top_companies.length > 0 || user.top_themes.length > 0">
                          <div class="col-md-6" *ngIf="user.top_companies.length > 0">
                            <strong class="detail-label">Top Companies:</strong>
                            <ul class="detail-list">
                              <li *ngFor="let c of user.top_companies">{{ c.name }} ({{ c.count }})</li>
                            </ul>
                          </div>
                          <div class="col-md-6" *ngIf="user.top_themes.length > 0">
                            <strong class="detail-label">Top Themes:</strong>
                            <ul class="detail-list">
                              <li *ngFor="let t of user.top_themes">{{ t.name }} ({{ t.count }})</li>
                            </ul>
                          </div>
                        </div>

                        <!-- Tabs table -->
                        <div *ngIf="expandedUserTabs.length > 0">
                          <strong class="detail-label mb-2" style="display: block;">All Tabs ({{ expandedUserTabs.length }})</strong>
                          <div class="tabs-table-container">
                            <table class="table table-sm table-bordered tabs-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Name</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr *ngFor="let tab of expandedUserTabs">
                                  <td>{{ tab.created_at || '-' }}</td>
                                  <td>{{ tab.tab_name || '-' }}</td>
                                  <td>
                                    <span class="badge" [ngClass]="tab.search_type === 'company' ? 'badge-company' : 'badge-theme'">
                                      {{ tab.search_type }}
                                    </span>
                                  </td>
                                  <td>
                                    <span [style.color]="tab.is_deleted ? '#dc3545' : '#28a745'">
                                      {{ tab.is_deleted ? 'Deleted' : 'Active' }}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div *ngIf="expandedUserTabs.length === 0" class="text-muted text-center py-2">
                          No tabs in this period.
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>
